

/* ══════════════ UTILITÁRIOS ══════════════ */
const brl = (n) => (n ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const num = (n, d = 0) => (n ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: d, maximumFractionDigits: d });
/**
 * Hoje é hoje.
 *
 * Era `new Date(2026, 7, 7, 9, 30)` — 7 de agosto de 2026, fixo. Fazia
 * sentido no protótipo, onde os dados de exemplo precisavam ser coerentes
 * entre si. Em produção, congelava o relógio do sistema inteiro:
 *
 *  - `atrasado()` comparava contra 7 de agosto, então conta vencida depois
 *    dessa data não aparecia como vencida;
 *  - vencimento padrão de conta nova (`dias(7)`) nascia no passado;
 *  - os gráficos de faturamento e de fluxo montavam os últimos seis meses
 *    terminando em agosto, e o mês corrente sumia.
 */
const HOJE = () => new Date();

/**
 * Data local, sem passar por UTC.
 *
 * `toISOString()` converte para UTC, e às 21h em Pouso Alegre (UTC-3) isso
 * já é o dia seguinte. A OS aberta à noite nasceria com a data de amanhã, e
 * o fechamento do dia não bateria.
 */
const dias = (n) => {
  const d = HOJE();
  d.setDate(d.getDate() + n);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};
const fdata = (s) => { if (!s) return "—"; const [a, m, d] = s.split("-"); return `${d}/${m}/${a}`; };
const uid = (p) => p + Math.random().toString(36).slice(2, 8).toUpperCase();
/**
 * Vencido é data que passou — não data que não existe.
 *
 * Sem a guarda, `"" < "2026-08-31"` é true em JavaScript: string vazia é
 * menor que qualquer data. Toda OS sem previsão de entrega aparecia com
 * "Previsão vencida em —", e todo lançamento sem vencimento entrava na conta
 * de vencidos, inflando o valor em atraso da tela financeira.
 */
const atrasado = (venc) => !!venc && venc < dias(0);






const ST = {
  orcamento: { l: "Orçamento", c: "p-mut" },
  aguardando: { l: "Aguardando aprovação", c: "p-sig" },
  aprovada: { l: "Aprovada", c: "p-info" },
  execucao: { l: "Em execução", c: "p-warn" },
  finalizada: { l: "Finalizada", c: "p-ok" },
  entregue: { l: "Entregue", c: "p-mut" },
  recusada: { l: "Recusada", c: "p-crit" },
};



const GRUPOS_CHECK = {
  "Freios": ["Pastilhas de freio", "Discos de freio", "Fluido de freio", "Freio de estacionamento"],
  "Motor e fluidos": ["Óleo do motor", "Líquido de arrefecimento", "Correias", "Vazamentos aparentes"],
  "Suspensão e direção": ["Suspensão dianteira", "Suspensão traseira", "Folga na direção", "Coifas e terminais"],
  "Pneus e rodas": ["Pneus / sulco", "Calibragem", "Estepe e macaco"],
  "Elétrica": ["Bateria", "Iluminação", "Alternador / carga", "Palhetas e limpador"],
};



/* Histórico de saídas dos últimos meses — base para giro, curva ABC,
   mínimo sugerido e detecção de peça parada. */


/* Orçamentos de balcão — nascem de um telefonema, não de um carro no pátio.
   Metade nunca vira serviço, e é justamente por isso que precisam de lugar
   próprio: misturados com OS, eles poluem o pátio e a taxa de conversão
   — que é o número que ninguém tem — fica invisível. */

const STO = {
  rascunho: { l: "Rascunho", c: "mut" },
  enviado:  { l: "Aguardando cliente", c: "sig" },
  aprovado: { l: "Aprovado", c: "ok" },
  recusado: { l: "Recusado", c: "crit" },
  expirado: { l: "Expirado", c: "mut" },
};

/* O motivo da recusa é a informação de negócio mais subestimada da oficina.
   Preço, prazo e concorrência são três problemas diferentes, com soluções
   diferentes — e sem registrar viram uma sensação vaga de "mês fraco". */
const MOTIVOS_RECUSA = [
  ["preco", "Achou caro"],
  ["prazo", "Prazo longo demais"],
  ["outra", "Foi em outra oficina"],
  ["adiou", "Adiou o serviço"],
  ["sem_resposta", "Não respondeu"],
];




/* ══════════════════════════════════════════════════════════════════
   COMPONENTES BASE
   ══════════════════════════════════════════════════════════════════ */

/** Rótulo em cima, valor embaixo. Para ficha de leitura, não de edição. */
const REGIME_ROT = {
  mei: "MEI", simples_nacional: "Simples Nacional",
  lucro_presumido: "Lucro Presumido", lucro_real: "Lucro Real",
};

/**
 * A alíquota de ISS vem do cadastro da oficina, não de constante.
 *
 * A tela mostrava `EMPRESA.iss` — 5% fixo do protótipo — mesmo depois de a
 * oficina configurar outra em Config → Fiscal. E `EMPRESA.icms`, 18%, era
 * pior: não existe coluna de ICMS em lugar nenhum, o número foi inventado.
 * Aparecia com valor em reais na tela de conferência, logo antes do botão de
 * transmitir.
 *
 * ICMS de peça depende de NCM, origem, destino e regime — quem calcula é o
 * provedor com a SEFAZ, não esta tela. Então ela não chuta: mostra a base e
 * diz de onde vem o imposto.
 */
const issDa = (of) => Number(of?.aliquota_iss ?? 0);
/** 3.5 vira "3,5" — o resto da tela usa vírgula, este campo não podia destoar. */
const pct = (n) => Number(n).toLocaleString("pt-BR", { maximumFractionDigits: 2 });
const regimeDe = (of) => REGIME_ROT[of?.regime] ?? "regime não informado";

const PED_ROT = { rascunho: "Rascunho", enviado: "Enviado", recebido: "Recebido", cancelado: "Cancelado" };
const PED_TOM = { rascunho: "mut", enviado: "sig", recebido: "ok", cancelado: "crit" };

/**
 * Formas de pagamento: o enum `forma_pagamento` do banco, com rótulo.
 *
 * Fonte única de propósito. O financeiro mantinha o seu próprio mapa, e nele
 * havia "cartao" — valor que o enum nunca teve, porque lá são `debito` e
 * `credito`. Dessa única divergência saíram três defeitos, nenhum com erro
 * visível para quem usa:
 *
 *   Lançar uma conta escolhendo "Cartão" estourava no INSERT, porque o
 *   Postgres recusa valor fora do enum — e a rota não valida antes, então o
 *   balconista via só uma falha genérica.
 *
 *   A coluna Forma saía EM BRANCO em todo título de cartão. E título de
 *   cartão é o que a OS mais gera.
 *
 *   O gráfico "Como o cliente paga" filtrava por `cartao`, que nunca casa
 *   com nada. A receita de cartão inteira ficava fora dele — numa oficina,
 *   costuma ser a maior fatia.
 *
 * Quem for mexer: `auditar_ligacoes.mjs` confere este mapa contra o enum do
 * `schema.sql` nos dois sentidos. Chave a mais quebra a gravação; forma do
 * banco sem rótulo aqui sai em branco na tela.
 */
const ROTULO_FORMA = {
  dinheiro: "Dinheiro em espécie",
  pix: "Pix",
  debito: "Cartão de débito",
  credito: "Cartão de crédito",
  boleto: "Boleto",
  transferencia: "Transferência",
  cheque: "Cheque",
  crediario: "Crediário",
  permuta: "Permuta",
};

/**
 * O que o balconista pode escolher — derivado do mapa acima, nunca uma lista
 * paralela, que é como as duas versões se separaram da primeira vez.
 *
 * Cheque, crediário e permuta ficam de fora da escolha e dentro do rótulo:
 * podem chegar por importação ou acerto antigo, e a tela precisa saber
 * mostrar o que existe mesmo sem oferecer. Passar a oferecer é decisão da
 * oficina, não consequência de um mapa.
 */
const FORMAS_ESCOLHIVEIS = ["dinheiro", "pix", "debito", "credito", "boleto", "transferencia"];

export { brl, num, HOJE, dias, fdata, uid, atrasado, ST, GRUPOS_CHECK, STO, MOTIVOS_RECUSA, REGIME_ROT, issDa, pct, regimeDe, PED_ROT, PED_TOM, ROTULO_FORMA, FORMAS_ESCOLHIVEIS };
